/* eslint-disable custom-rules/no-color-hex */
import React, { useEffect, useState } from 'react';
import { AvButton, loadingPage, TextInput } from '@fabric-ui/components';
import { FullLogo } from '@fabric-ui/icons';
import loginBg from '@fabric-ui/icons/svg/login_bg.svg';
import { flex } from '@fabric-ui/theme';
import { envVariables } from '@fabric-ui/utils';
import { alpha, Box } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { configureAmplify, isZidSupported, signIn } from '@utils/auth.utils';
import { useNavigate, useSearchParams } from '@utils/AvRouter';
import { AuthStorageKeys } from '@utils/localStorageAuthUtils.utils';
import { AuthConfiguration, AuthConfigurationV2, AuthPlatform } from '../auth/auth.types';
import { getAuthProviderByType } from '../auth/authenticationProviderFactory';

// AuthConfiguration type is for when ZID is not supported
interface LoginProps {
  setIsCurrentlyLoggingIn: (passed: boolean) => void;
  setAuthConfig: (config: AuthConfigurationV2 | AuthConfiguration) => void;
}

const Login: React.FC<LoginProps> = () => {
  const [search] = useSearchParams();
  const domainParam = search.domain;
  const loginHint = search.login_hint;
  const [email, setEmail] = useState();
  const [startLoading, setStartLoading] = useState(false);
  const navigate = useNavigate();

  const {
    isPending,
    error,
    mutate: submit,
  } = useMutation({
    mutationKey: ['auth-config', email, domainParam],
    mutationFn: () => loginFunc(),
  });

  const loginFunc = () => {
    if (isZidSupported()) {
      return fetch(
        `${envVariables.VITE_WEBSERVER_API_URL}/auth-config/${
          domainParam ? `domain-login?domain=${domainParam}` : loginHint ? `v2/login?email=${loginHint}` : `v2/login?email=${email}`
        }`
      ).then(async res => {
        const result = await res.json();
        if (domainParam) {
          await navigate({ to: '/' });
        }
        if (res.ok) {
          const config = {
            provider: result.provider || AuthPlatform.COGNITO,
            connectionDetails: result.connectionDetails || result,
          };
          localStorage.setItem(AuthStorageKeys.authConfig, JSON.stringify(config));
          const authPlatform = getAuthProviderByType(config.provider);
          authPlatform.configureAuthProvider();
          setStartLoading(true);

          await authPlatform.signIn();
        }

        throw result;
      });
    }
    return fetch(
      `${envVariables.VITE_WEBSERVER_API_URL}/auth-config/${domainParam ? `domain-login?domain=${domainParam}` : `login?email=${email}`}`
    ).then(async res => {
      const result = await res.json();
      if (domainParam) {
        await navigate({ to: '/' });
      }
      if (res.ok) {
        localStorage.setItem(AuthStorageKeys.authConfig, JSON.stringify(result));
        configureAmplify(result);
        setStartLoading(true);
        await signIn();
      }
      throw result;
    });
  };

  useEffect(() => {
    if (domainParam || loginHint) {
      submit();
    }
  }, []);

  const style = {
    ...flex.center,
    backgroundImage: `url(${loginBg}), linear-gradient(67deg, #081837 31.72%, #13367C 86.03%)`,
    backgroundSize: 'cover',
    backgroundBlendMode: 'overlay',
    form: {
      ...flex.colCenter,
      gap: 3,
      background: alpha('#FFFFFF', 0.1),
      backdropFilter: 'blur(25px)',
      width: 430,
      padding: 5,
      borderRadius: '2px',
      '.MuiOutlinedInput-root': {
        color: '#FFFFFF',
        backgroundColor: 'transparent !important',
        '.MuiOutlinedInput-notchedOutline': {
          borderColor: alpha('#FFFFFF', 0.2),
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderColor: '#98B9FA',
        },
      },
      '.MuiInputLabel-root': {
        color: '#5F687A',
        '&.Mui-focused': {
          color: '#98B9FA',
        },
      },
      '.MuiIconButton-root': {
        color: '#949EB2',
        ':hover:before': {
          backgroundColor: 'transparent',
        },
      },
      '.MuiButton-root.Mui-disabled': {
        backgroundColor: 'var(--color-surface-interactive-primary-active)',
        svg: { color: 'var(--color-content-inverted-base-primary) !important' },
      },
    },
  };

  return startLoading
    ? loadingPage()
    : !domainParam && !loginHint && (
        <Box component="main" sx={style}>
          <form
            className="fluid-container"
            onSubmit={e => {
              e.preventDefault();
              submit();
            }}>
            <FullLogo style={{ width: 180, height: 38 }} />
            <TextInput
              type="email"
              label="Email address"
              autoCorrect="false"
              value={email}
              inputMode="email"
              autoComplete="email"
              autoCapitalize="none"
              spellCheck="false"
              onChange={setEmail}
              error={(error as any)?.message}
              sx={{ maxWidth: 'unset' }}
            />
            <AvButton variant="contained" type="submit" fullWidth loading={isPending}>
              Continue
            </AvButton>
          </form>
        </Box>
      );
};

export default Login;
